import api from "@/plugins/api";
import { appendQuery } from "@/shared/utils/url";
const BASE_PATH = "campaigns";
function parseEtag(header) {
    if (!header)
        return 0;
    const match = /"?(\d+)"?/.exec(header);
    return match ? Number(match[1]) : 0;
}
function formatEtag(version) {
    return `"${version}"`;
}
/**
 * API client for campaign operations.
 * All calls go through the BFF (unwrapEnvelope: true).
 */
export const campaignsApi = {
    /**
     * Gets a paginated list of campaigns with optional filters.
     */
    async getCampaigns(params = {}) {
        const url = appendQuery(BASE_PATH, {
            page: params.page,
            pageSize: params.pageSize,
            status: params.status,
            type: params.type,
            search: params.search,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        });
        const response = await api.get(url, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Gets the active email provider's non-secret sender configuration (OP-6207).
     * Used by the action editor to prefill the sender and prevent a domain mismatch.
     */
    async getEmailSenderConfig() {
        const response = await api.get("opink-email-provider/sender-config", {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Gets a campaign by ID.
     */
    async getCampaignById(id) {
        const response = await api.get(`${BASE_PATH}/${id}`, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /** The campaign's funded projects, enriched by the BFF with their OPROJ status and OPDON total. */
    async getCampaignProjects(id) {
        const response = await api.get(`${BASE_PATH}/${id}/projects`, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Creates a new campaign.
     */
    async createCampaign(data) {
        const response = await api.post(BASE_PATH, data, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Quick-creates an Essential single-channel campaign (US-3.25):
     * campaign (Draft) + selected default plan + one empty Draft action.
     */
    async quickCreateCampaign(data) {
        const response = await api.post(`${BASE_PATH}/quick`, data, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Updates a campaign.
     */
    async updateCampaign(id, data) {
        const response = await api.put(`${BASE_PATH}/${id}`, data, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Archives a campaign (soft-delete).
     */
    async archiveCampaign(id) {
        await api.delete(`${BASE_PATH}/${id}`, { unwrapEnvelope: true });
    },
    /**
     * Adds a communication action to a campaign's plan.
     */
    async addAction(campaignId, data) {
        const response = await api.post(`${BASE_PATH}/${campaignId}/communication-plan/actions`, data, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Updates a communication action's email editor fields.
     */
    async updateAction(campaignId, actionId, data) {
        const response = await api.put(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}`, data, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Updates a communication action's status.
     */
    async updateActionStatus(campaignId, actionId, data) {
        const response = await api.patch(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}/status`, data, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Removes a communication action from a campaign's plan.
     */
    async removeAction(campaignId, actionId) {
        await api.delete(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}`, {
            unwrapEnvelope: true,
        });
    },
    /**
     * Duplicates a communication action within the same campaign plan.
     * Backend returns 201 + ApiResponse<CommunicationActionModel> (envelope unwrapped by interceptor).
     */
    async duplicateAction(campaignId, actionId) {
        const response = await api.post(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}/duplicate`, {}, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Creates the follow-up ("relance des non-donateurs") of a sent email, as proposed by the Pilotage
     * recommendation R1. The backend copies the origin's sender and audience, adds the "has not
     * donated to this campaign" filter and places the new draft at the end of the origin's group.
     *
     * `actionName` is sent from here because it is user-visible copy: the backend must not invent a
     * name in one language for a fr/nl/en product (R76).
     */
    async createFollowUpAction(campaignId, actionId, actionName) {
        const response = await api.post(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}/follow-up`, { actionName }, { unwrapEnvelope: true });
        return response.data;
    },
    // --- Communication Action Groups (US-3.21) ---
    /**
     * Creates a root-level group in a campaign's communication plan.
     */
    async createGroup(campaignId, body) {
        const response = await api.post(`${BASE_PATH}/${campaignId}/communication-plan/groups`, body, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Renames a root-level group in a campaign's communication plan.
     */
    async renameGroup(campaignId, groupId, body) {
        const response = await api.put(`${BASE_PATH}/${campaignId}/communication-plan/groups/${groupId}`, body, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Removes a root-level group and cascade-deletes its (all-Draft) actions.
     */
    async removeGroup(campaignId, groupId) {
        const response = await api.delete(`${BASE_PATH}/${campaignId}/communication-plan/groups/${groupId}`, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Reorders the root-level items (groups and orphan actions) of a campaign's communication plan.
     */
    async reorderRootItems(campaignId, body) {
        await api.put(`${BASE_PATH}/${campaignId}/communication-plan/root-items/order`, body, { unwrapEnvelope: true });
    },
    /**
     * Moves a communication action to a different group (or to the root level) and position.
     * Returns the refreshed campaign so the caller can re-render the full plan in one round-trip.
     */
    async moveAction(campaignId, actionId, body) {
        const response = await api.put(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}/move`, body, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Renames a communication action inline (US-3.21).
     */
    async renameAction(campaignId, actionId, body) {
        const response = await api.patch(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}`, body, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Reorders communication actions WITHIN a single group. Calls PUT .../actions/order with
     * { orderedActionIds, groupId }. Root-level (orphan) reorders use reorderRootItems instead —
     * the backend's `groupId:null` reorder expects every action in the plan.
     */
    async reorderInContainer(campaignId, body) {
        await api.put(`${BASE_PATH}/${campaignId}/communication-plan/actions/order`, body, { unwrapEnvelope: true });
    },
    /**
     * Gets the audience summary preview (US-11.4.a). Draft → BFF resolves live via OPHUB;
     * Saved → BFF returns the frozen local snapshot.
     */
    async getAudiencePreview(campaignId) {
        const response = await api.get(`${BASE_PATH}/${campaignId}/audience/preview`, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Sends a test email for a communication action.
     */
    async sendTestEmail(campaignId, actionId, data) {
        await api.post(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}/send-test`, data, { unwrapEnvelope: true });
    },
    async sendAction(campaignId, actionId) {
        const response = await api.post(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}/send`, {}, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Gets the list of available time zones.
     */
    async getAvailableTimeZones() {
        const response = await api.get("/available-time-zones", {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    // ── Plan Template Operations ─────────────────────────────────
    /**
     * Gets a paginated list of communication plan templates.
     */
    async getPlanTemplates(params = {}) {
        const url = appendQuery("communication-plan-templates", {
            page: params.page,
            pageSize: params.pageSize,
            type: params.type,
            search: params.search,
        });
        const response = await api.get(url, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Gets a plan template by ID with its pre-configured actions.
     */
    async getPlanTemplateById(id) {
        const response = await api.get(`communication-plan-templates/${id}`, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Creates a new campaign from a pre-configured plan template.
     */
    async createCampaignFromPlanTemplate(data) {
        const response = await api.post(`${BASE_PATH}/from-plan-template`, data, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    // --- Analytics (US-4.1) ---
    /**
     * Gets delivery KPIs for a single communication action.
     */
    async getActionStats(campaignId, actionId) {
        const response = await api.get(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}/stats`, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Gets the delivery KPIs of the WHOLE campaign, already aggregated across its actions by OPINK
     * (US-2.7). Distinct from `getCampaignActionsStats`, which returns one row per action and leaves
     * the summing to the caller: the Statistiques deliverability widget states campaign-level rates,
     * and re-deriving them client-side from the per-action rows would disagree with the backend's own
     * aggregate the moment an action is excluded server-side.
     */
    async getCampaignStats(campaignId) {
        const response = await api.get(`${BASE_PATH}/${campaignId}/stats`, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Gets delivery KPIs for EVERY action of a campaign in one request. The Pilotage feed needs a row
     * per action; calling the per-action endpoint N times would cost N round-trips.
     */
    async getCampaignActionsStats(campaignId) {
        const response = await api.get(`${BASE_PATH}/${campaignId}/communication-plan/actions/stats`, { unwrapEnvelope: true });
        return response.data;
    },
    // --- Recommendation dismissals ("Ignorer", Pilotage) ---
    /**
     * Lists the recommendation keys silenced on a campaign. An empty array is the normal state of a
     * campaign nobody has dismissed anything on — never an error.
     */
    async getRecommendationDismissals(campaignId) {
        const response = await api.get(`${BASE_PATH}/${campaignId}/recommendation-dismissals`, { unwrapEnvelope: true });
        return response.data.dismissedKeys ?? [];
    },
    /**
     * Silences a recommendation and returns the FULL resulting set, so the caller replaces its state
     * rather than appending to it — that way a dismissal made in another tab is picked up in the same
     * round-trip. Idempotent server-side: re-dismissing a key is a success, not a conflict.
     */
    async dismissRecommendation(campaignId, recommendationKey) {
        const response = await api.post(`${BASE_PATH}/${campaignId}/recommendation-dismissals`, { recommendationKey }, { unwrapEnvelope: true });
        return response.data.dismissedKeys ?? [];
    },
    // --- Donation performance (US-8.8 / OP-6670, Pilotage) ---
    /**
     * Gets the campaign donation performance read-model (rollup + per-objective progress +
     * per-project breakdown). `comparisonWindowDays` drives the "évolution vs sem. dernière" delta; when
     * omitted the backend applies its default (7 days). A campaign with no snapshot returns a
     * zeroed projection (never 404 — R85).
     */
    async getDonationPerformance(campaignId, comparisonWindowDays) {
        const url = appendQuery(`${BASE_PATH}/${campaignId}/donation-performance`, {
            comparisonWindowDays,
        });
        const response = await api.get(url, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Gets the campaign's collection split by the channel each donation came in through (US-2.4).
     *
     * Unlike its siblings above this one resolves against OPDON's own donations rather than the OPINK
     * snapshots, so its figures are live: they can differ from the totals rendered elsewhere on the
     * page for as long as the OPINK projection lags. A campaign with no Validated donation returns an
     * empty `channels` list (never 404 — R85).
     */
    async getDonationChannelBreakdown(campaignId) {
        const response = await api.get(`${BASE_PATH}/${campaignId}/donation-channel-breakdown`, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Gets the campaign's collection split by audience source (US-2.5).
     *
     * The rows are computed live from OPHUB membership and OPDON donations, so they are current but
     * NOT a breakdown of the campaign total: a contact in two segments counts in both, and donations
     * with no contact behind them (organisations, donors not yet reconciled) belong to no segment. A
     * campaign whose audience is not frozen yet returns an empty `segments` list (never 404 - R85).
     */
    async getSegmentPerformance(campaignId) {
        const response = await api.get(`${BASE_PATH}/${campaignId}/segment-performance`, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Gets the cumulative donation timeseries for a campaign.
     *
     * `interval` has NO server default: the OPINK controller forwards `interval ?? string.Empty` and
     * the query rejects anything that is not `hour`/`day`, so omitting it is an HTTP 400. Both bounds
     * are equally mandatory in practice — the `from <= to` and 366-day rules are each conditional on
     * BOTH values being present, so a lone `from` is unbounded.
     *
     * The caller must pass UTC instants suffixed `Z`: the BFF→OPINK hop loses `DateTimeKind` (the
     * RestEase connector declares `[Query] DateTime?` with no `Format` and serializes via
     * `ToString()`), and OPINK re-reads them with `EnsureUtc()`.
     */
    async getDonationTimeSeries(campaignId, params) {
        const url = appendQuery(`${BASE_PATH}/${campaignId}/donation-aggregate/timeseries`, {
            interval: params.interval,
            from: params.from,
            to: params.to,
        });
        const response = await api.get(url, { unwrapEnvelope: true });
        return response.data;
    },
    // --- Timeline (US-4.2) ---
    /**
     * Gets delivery timeline data for an entire campaign.
     */
    async getCampaignTimeline(campaignId, params = {}) {
        const url = appendQuery(`${BASE_PATH}/${campaignId}/timeline`, {
            granularity: params.granularity,
            from: params.from,
            to: params.to,
            metrics: params.metrics,
        });
        const response = await api.get(url, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Gets delivery timeline data for a single action.
     */
    async getActionTimeline(campaignId, actionId, params = {}) {
        const url = appendQuery(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}/timeline`, {
            granularity: params.granularity,
            from: params.from,
            to: params.to,
            metrics: params.metrics,
        });
        const response = await api.get(url, { unwrapEnvelope: true });
        return response.data;
    },
    // --- Top Links (US-4.3) ---
    /**
     * Gets top clicked links for an entire campaign.
     */
    async getCampaignTopLinks(campaignId, limit = 20) {
        const response = await api.get(`${BASE_PATH}/${campaignId}/links?limit=${limit}`, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Gets top clicked links for a single action.
     */
    async getActionTopLinks(campaignId, actionId, limit = 20) {
        const response = await api.get(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}/links?limit=${limit}`, { unwrapEnvelope: true });
        return response.data;
    },
    // --- Bounces (US-4.4) ---
    /**
     * Gets a paginated list of bounced messages for a campaign.
     */
    async getBounces(campaignId, params = {}) {
        const url = appendQuery(`${BASE_PATH}/${campaignId}/bounces`, {
            page: params.page,
            pageSize: params.pageSize,
            type: params.type,
        });
        const response = await api.get(url, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Downloads the bounces CSV export as a Blob; the caller is responsible for triggering the download.
     * Goes through the shared API client so auth, retry, and telemetry stay centralized.
     */
    async exportBounces(campaignId, params = {}) {
        const url = appendQuery(`${BASE_PATH}/${campaignId}/bounces/export`, {
            type: params.type,
        });
        const response = await api.get(url, {
            unwrapEnvelope: true,
            responseType: "blob",
        });
        const disposition = response.headers["content-disposition"];
        const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] ?? `bounces-${campaignId}.csv`;
        return { blob: response.data, filename };
    },
    // --- Audience composition (US-11.2 / 11.6 / 11.10 / 11.3) ---
    async getAudience(campaignId) {
        const response = await api.get(`${BASE_PATH}/${campaignId}/audience`, {
            unwrapEnvelope: true,
        });
        return { data: response.data, version: parseEtag(response.headers.etag) };
    },
    async putAudience(campaignId, body, version) {
        const response = await api.put(`${BASE_PATH}/${campaignId}/audience`, body, { unwrapEnvelope: true, headers: { "If-Match": formatEtag(version) } });
        return { data: response.data, version: parseEtag(response.headers.etag) };
    },
    // Freeze audience (Phase 3 — provisions frozen segments; replaces validate as the Save action)
    async freezeAudience(campaignId) {
        const response = await api.post(`${BASE_PATH}/${campaignId}/audience/freeze`, {}, { unwrapEnvelope: true });
        return response.data;
    },
    async getAudienceJob(campaignId, jobId, signal) {
        const response = await api.get(`${BASE_PATH}/${campaignId}/audience/jobs/${jobId}`, { unwrapEnvelope: true, signal });
        return response.data;
    },
    async getAudienceSnapshot(campaignId, params) {
        const query = new URLSearchParams({
            page: String(params.page),
            pageSize: String(params.pageSize),
        });
        if (params.search && params.search.trim().length > 0) {
            query.set("search", params.search.trim());
        }
        // R61: wire param names match the BFF [FromQuery(Name = "segmentId"/"manualOnly"/"excludedOnly")] bindings.
        if (params.segmentId) {
            query.set("segmentId", params.segmentId);
        }
        if (params.manualOnly) {
            query.set("manualOnly", "true");
        }
        if (params.excludedOnly) {
            query.set("excludedOnly", "true");
        }
        // R61: the wire name must be exactly `criteria` — the BFF binds it via
        // [FromQuery(Name = "criteria")] and JSON-decodes it into a FilterNode.
        if (params.criteria) {
            query.set("criteria", JSON.stringify(params.criteria));
        }
        const response = await api.get(`${BASE_PATH}/${campaignId}/audience/snapshot?${query.toString()}`, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Fetches the raw audience composition (segment selections + manual contact ids) for a campaign.
     * Called on page load to seed the picker's checkbox state without a separate search+diff (US-11.20).
     * Returns 404 for campaigns with no audience aggregate yet (treat as empty composition).
     */
    async getAudienceComposition(campaignId) {
        const response = await api.get(`${BASE_PATH}/${campaignId}/audience/composition`, { unwrapEnvelope: true });
        return response.data;
    },
    // --- Manual contact exclusion (US-11.17) ---
    /**
     * Excludes contacts from the campaign audience (US-11.17). Draft-only; capped at 50 server-side
     * (422 `Campaign.AudienceExclusions.LimitReached`).
     */
    async postAudienceExclusions(campaignId, body) {
        const response = await api.post(`${BASE_PATH}/${campaignId}/audience/exclusions`, body, { unwrapEnvelope: true });
        return response.data;
    },
    /** Re-includes a manually excluded contact (US-11.17). Idempotent. */
    async deleteAudienceExclusion(campaignId, contactId) {
        await api.delete(`${BASE_PATH}/${campaignId}/audience/exclusions/${contactId}`, {
            unwrapEnvelope: true,
        });
    },
    /**
     * Membership check for the Manual-tab badge (US-11.17): returns which of the candidate contact ids
     * are members of the campaign's net audience.
     */
    async checkAudienceMembership(campaignId, contactIds) {
        const response = await api.post(`${BASE_PATH}/${campaignId}/audience/membership-check`, { contactIds }, { unwrapEnvelope: true });
        return response.data.memberContactIds;
    },
    // --- Per-action recipient selection (US-11.12) ---
    /**
     * Gets the recipient selection for a specific communication action.
     */
    async getRecipientSelection(campaignId, actionId) {
        const response = await api.get(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}/recipient-selection`, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Sets (replaces) the recipient selection for a specific communication action.
     */
    async setRecipientSelection(campaignId, actionId, payload) {
        const response = await api.put(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}/recipient-selection`, payload, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Resolves the deduplicated recipient count for a given selection (Phase 3 OP-6056).
     * `criteria` optionally narrows the count by the action's advanced audience filter.
     * POST `/api/v1/campaigns/{campaignId}/audience/recipient-count`
     */
    async getRecipientCount(campaignId, payload) {
        const response = await api.post(`${BASE_PATH}/${campaignId}/audience/recipient-count`, payload, { unwrapEnvelope: true });
        return response.data;
    },
    // --- Advanced audience filter ---
    /**
     * Replaces the action's advanced audience filter.
     * PUT `/campaigns/{campaignId}/communication-plan/actions/{actionId}/audience-filter`
     */
    async setAudienceFilter(campaignId, actionId, payload) {
        const response = await api.put(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}/audience-filter`, payload, { unwrapEnvelope: true });
        return response.data;
    },
    /** Removes the action's advanced audience filter. */
    async clearAudienceFilter(campaignId, actionId) {
        await api.delete(`${BASE_PATH}/${campaignId}/communication-plan/actions/${actionId}/audience-filter`, { unwrapEnvelope: true });
    },
    // --- Segments listing (US-11.1 BFF proxy) ---
    async getSegments(params = {}) {
        const query = new URLSearchParams();
        query.set("objectType", "Contact");
        query.set("status", "Active");
        if (params.search)
            query.set("search", params.search);
        if (params.page != null)
            query.set("page", String(params.page));
        if (params.pageSize != null)
            query.set("pageSize", String(params.pageSize));
        if (params.sortBy)
            query.set("sortBy", params.sortBy);
        if (params.sortDirection)
            query.set("sortDirection", params.sortDirection);
        const response = await api.get(`ophub-segments?${query.toString()}`, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Searches OPHUB contacts for the audience manual-contact picker (US-11.20).
     * Calls GET /contacts (BFF → OPHUB tenant-wide search) with the enriched projection
     * fields added in PR-1 (organisationName, function, category, primaryEmail).
     * NO `fields=` param — the typed contact-list path always returns the full projection.
     * Maps the ApiPaginatedResponse envelope to PaginatedResult<ManualContactListItem>.
     *
     * NOTE: no `unwrapEnvelope: true` is passed here intentionally. The response interceptor
     * in api.ts strips the BFF ApiResponse<T> envelope only when that flag is set; omitting it
     * hands back the body untouched. This matches the sibling pattern in contactApi.ts
     * (`fetchContacts`, `searchContactsByName`), which also call GET /contacts without the flag
     * and read `response.data` as the raw ApiPaginatedResponse. Do NOT add `unwrapEnvelope: true`
     * here without also switching to reading the interceptor-unwrapped shape — it would
     * double-unwrap and break the picker.
     */
    async searchAudienceContacts(params = {}) {
        const response = await api.get("/contacts", {
            params: {
                search: params.search || undefined,
                page: params.page ?? 1,
                per_page: params.pageSize,
                // OPHUB ContactSortResolver only recognises snake_case tokens (last_name/first_name/
                // fullName/birth_date/created_at); any other value silently falls back to created_at
                // desc and ignores sortOrder. Must be "last_name", not the camelCase "lastName".
                sortBy: "last_name",
                sortOrder: params.sortDirection,
            },
        });
        const raw = response.data;
        const items = (raw.data ?? []).map((row) => ({
            id: row.id,
            firstName: row.firstName,
            lastName: row.lastName,
            avatarUrl: row.avatarUrl,
            category: row.category,
            function: row.function,
            organisationName: row.organisationName,
            email: row.primaryEmail,
        }));
        return {
            items,
            page: raw.pagination?.page ?? params.page ?? 1,
            pageSize: raw.pagination?.perPage ?? params.pageSize ?? 20,
            totalCount: raw.pagination?.totalItems ?? 0,
            totalPages: raw.pagination?.totalPages ?? 0,
        };
    },
};
